import axios from "axios";
import axiosInstance from "utils/axiosInstance";
import { WarningNetworkFailure } from "helpers/alertsweet";
import { PRESIGNED_URL } from "constants/api";

const BASE_URL = (
  process.env.REACT_APP_MAIN_API_URL ||
  process.env.REACT_APP_BASE_API_URL ||
  ""
).replace(/\/$/, "");
const DONATE_URL = `${BASE_URL}/donate`;

const getAuthHeaders = (token: string, businessId?: string) => ({
  authorization: "Bearer " + token,
  ...(businessId ? { b: businessId } : {}),
});

const getQueryParams = (params: Record<string, any> = {}) => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    queryParams.set(key, String(value));
  });

  return queryParams.toString();
};

const getFileType = (file: File) => {
  if (file.type) return file.type;

  const extension = file.name.split(".").pop()?.toLowerCase();
  const fallbackTypes: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    heic: "image/heic",
    heif: "image/heif",
  };

  return fallbackTypes[extension || ""] || "application/octet-stream";
};

const getPresignData = (response: any) =>
  response?.data?.data || response?.data || {};

const getStableUploadedUrl = (presignData: any, uploadUrl: string) => {
  const stableUrl =
    presignData?.publicUrl ||
    presignData?.publicURL ||
    presignData?.fileUrl ||
    presignData?.fileURL ||
    presignData?.stableUrl ||
    presignData?.location;

  if (stableUrl) return stableUrl;

  return uploadUrl.split("?")[0];
};

export const getUserProfileData = (response: any) =>
  response?.data?.user ||
  response?.data?.data ||
  response?.data ||
  response?.user ||
  response ||
  {};

export type UserKycPayload = {
  fullName: string;
  dateOfBirth: string;
  address: string;
  identityNo: string;
  documentFrontImage: string;
  documentBackImage: string;
  selfieWithDocumentImage: string;
  bankAccountNo: string;
  bankAccountName: string;
  bankName: string;
};

export type UserChangePasswordPayload = {
  oldPassword: string;
  newPassword: string;
};

export type UserResetPasswordPayload = {
  userId?: string;
  email?: string;
  phone?: string;
  password: string;
};

export type StreamerProfilePayload = {
  name?: string;
  image?: string;
  coverImage?: string;
  bio?: string;
  isOnline?: boolean;
  socialLinks?: Record<string, string>;
  donationSettings?: {
    currency?: string;
    minAmount?: number;
    maxAmount?: number;
    suggestedAmounts?: number[];
    allowedPaymentMethods?: string[];
    allowOfflineDonations?: boolean;
  };
};

type UploadProfileImageParams = {
  file: File;
  token: string;
  businessId?: string;
  onProgress?: (progress: number) => void;
};

export const userGetOne = async ({
  id,
  token,
  businessId,
}: {
  id: string;
  token: string;
  businessId: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/user/get-one/${id}`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const userGetMany = async ({
  findby = {},
  token,
  businessId,
  skip = 0,
  limit = 50,
}: {
  findby?: Record<string, any>;
  token: string;
  businessId: string;
  skip?: number;
  limit?: number;
}): Promise<any[]> => {
  try {
    const queryParams = getQueryParams({
      ...findby,
      skip,
      limit,
    });
    const url = `${DONATE_URL}/user/get-many?${queryParams}`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const userGetCount = async ({
  findby = {},
  token,
  businessId,
}: {
  findby?: Record<string, any>;
  token: string;
  businessId: string;
}): Promise<{ count: number }> => {
  try {
    const queryParams = getQueryParams(findby);
    const url = `${DONATE_URL}/user/get-count?${queryParams}`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const userCreateOne = async (
  body: any,
  token: string,
  businessId: string,
): Promise<any> => {
  try {
    const url = `${DONATE_URL}/user/create-one`;
    const res = await axiosInstance.post(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const userUpdateOne = async (
  body: any,
  token: string,
  businessId: string,
): Promise<any> => {
  try {
    const url = `${DONATE_URL}/user/update-one`;
    const res = await axiosInstance.put(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const userDeleteOne = async ({
  id,
  token,
  businessId,
}: {
  id: string;
  token: string;
  businessId: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/user/delete-one/${id}`;
    const res = await axiosInstance.delete(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const userKycSubmit = async ({
  body,
  token,
  businessId,
}: {
  body: UserKycPayload;
  token: string;
  businessId: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/user/kyc/submit`;
    const res = await axiosInstance.post(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const userKycGetMe = async ({
  token,
  businessId,
}: {
  token: string;
  businessId: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/user/kyc/me`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const userKycUpdate = async ({
  body,
  token,
  businessId,
}: {
  body: UserKycPayload;
  token: string;
  businessId: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/user/kyc/update`;
    const res = await axiosInstance.put(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const userKycGetStatus = async ({
  token,
  businessId,
}: {
  token: string;
  businessId: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/user/kyc/status`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const userChangePassword = async ({
  body,
  token,
  businessId,
}: {
  body: UserChangePasswordPayload;
  token: string;
  businessId: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/user/change-password`;
    const res = await axiosInstance.post(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const userResetPassword = async ({
  body,
  token,
  businessId,
}: {
  body: UserResetPasswordPayload;
  token: string;
  businessId: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/user/reset-password`;
    const res = await axiosInstance.post(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const userGetMe = async ({
  token,
  businessId,
}: {
  token: string;
  businessId?: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/auth/get-me`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const userUpdateMe = async ({
  body,
  token,
  businessId,
}: {
  body: any;
  token: string;
  businessId: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/user/me`;
    const res = await axiosInstance.put(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const donateStreamerUpdateProfile = async ({
  body,
  token,
  businessId,
}: {
  body: StreamerProfilePayload;
  token: string;
  businessId?: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/streamer/update-profile`;
    const res = await axiosInstance.put(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateStreamerUpdateProfile error", error);
    throw error;
  }
};

export const uploadDonateProfileImage = async ({
  file,
  token,
  businessId,
  onProgress,
}: UploadProfileImageParams): Promise<{ url: string; name: string }> => {
  try {
    const fileType = getFileType(file);
    const presignResponse = await axiosInstance.post(
      PRESIGNED_URL,
      {
        name: fileType,
        fileName: file.name,
        fileType,
      },
      {
        headers: getAuthHeaders(token, businessId),
      },
    );
    const presignData = getPresignData(presignResponse);
    const uploadUrl = presignData?.url || presignData?.uploadUrl;

    if (!uploadUrl) {
      throw new Error("Upload URL is required");
    }

    await axios.put(uploadUrl, file, {
      timeout: 120000,
      headers: {
        "Content-Type": fileType,
      },
      onUploadProgress: (progressEvent) => {
        if (!progressEvent.total || !onProgress) return;
        onProgress(
          Math.round((progressEvent.loaded * 100) / progressEvent.total),
        );
      },
    });

    const url = getStableUploadedUrl(presignData, uploadUrl);
    const splitName = url.split("/");
    const name = splitName[splitName.length - 1];

    return { url, name };
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("uploadDonateProfileImage error", error);
    throw error;
  }
};

export const userWalletGetMe = async ({
  token,
  businessId,
}: {
  token: string;
  businessId?: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/user/wallet/me`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const roleGetMany = async ({
  findby = {},
  token,
  businessId,
}: {
  findby?: Record<string, any>;
  token: string;
  businessId: string;
}): Promise<any[]> => {
  try {
    const queryParams = getQueryParams(findby);
    const url = `${DONATE_URL}/role/get-many${
      queryParams ? `?${queryParams}` : ""
    }`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};

export const userUpdateRole = async ({
  id,
  body,
  token,
  businessId,
}: {
  id: string;
  body: { role: string };
  token: string;
  businessId: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/user/update-role/${id}`;
    const res = await axiosInstance.put(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("error", error);
    throw error;
  }
};
